import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import ResponsiveNavigation from '../components/ResponsiveNavigation';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { FileText, Upload, Eye, Trash2, Check, Loader2, User, MapPin, DollarSign, Briefcase } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const ProspectProfile = () => {
  const { userProfile, refreshProfile } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showCvViewer, setShowCvViewer] = useState(false);

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    location: '',
    currentJobTitle: '',
    currentCompany: '',
    experienceLevel: '',
    experienceYears: '',
    workArrangement: '',
    availability: '',
    expectedSalaryMin: '',
    salaryCurrency: 'EUR',
    linkedinUrl: '',
    bio: '',
    skills: '',
    languages: '',
    resumeUrl: '',
    resumeFilename: '',
  });

  useEffect(() => {
    if (!userProfile?.id) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/prospects/user/${userProfile.id}`);
        if (r.data.success) {
          const p = r.data.data;
          setForm({
            firstName: p.firstName || '',
            lastName: p.lastName || '',
            email: p.email || '',
            phone: p.phone || '',
            location: p.location || '',
            currentJobTitle: p.currentJobTitle || '',
            currentCompany: p.currentCompany || '',
            experienceLevel: p.experienceLevel || '',
            experienceYears: p.experienceYears?.toString() || '',
            workArrangement: p.workArrangement || p.remotePreference || '',
            availability: p.availability || '',
            expectedSalaryMin: p.expectedSalaryMin?.toString() || '',
            salaryCurrency: p.salaryCurrency || 'EUR',
            linkedinUrl: p.linkedinUrl || '',
            bio: p.bio || '',
            skills: Array.isArray(p.skills) ? p.skills.join(', ') : '',
            languages: Array.isArray(p.languages) ? p.languages.join(', ') : '',
            resumeUrl: p.resumeUrl || '',
            resumeFilename: p.resumeFilename || '',
          });
        }
      } catch (e) {
        console.error('Failed to load profile', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [userProfile?.id]);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const uploadResume = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File must be under 10 MB');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('resume', file);
      const r = await axios.post(`${API}/api/public/upload-resume`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = r.data?.data?.resumeUrl;
      if (!url) throw new Error('No URL returned');
      setForm((f) => ({ ...f, resumeUrl: url, resumeFilename: file.name }));
      toast.success('CV uploaded — remember to save your profile');
    } catch (e) {
      toast.error('Upload failed. Try again.');
    } finally {
      setUploading(false);
    }
  };

  const removeResume = () => {
    setForm((f) => ({ ...f, resumeUrl: '', resumeFilename: '' }));
  };

  const handleSave = async () => {
    if (!userProfile?.id) return;
    setSaving(true);
    try {
      const payload = {
        firstName: form.firstName,
        lastName: form.lastName,
        phone: form.phone,
        location: form.location,
        currentJobTitle: form.currentJobTitle,
        currentCompany: form.currentCompany,
        experienceLevel: form.experienceLevel || null,
        experienceYears: form.experienceYears ? parseInt(form.experienceYears, 10) : null,
        workArrangement: form.workArrangement || null,
        availability: form.availability || null,
        expectedSalaryMin: form.expectedSalaryMin ? parseInt(form.expectedSalaryMin, 10) : null,
        salaryCurrency: form.salaryCurrency || 'EUR',
        linkedinUrl: form.linkedinUrl,
        bio: form.bio,
        skills: form.skills
          ? form.skills.split(',').map((s) => s.trim()).filter(Boolean)
          : [],
        languages: form.languages
          ? form.languages.split(',').map((s) => s.trim()).filter(Boolean)
          : [],
        resumeUrl: form.resumeUrl || null,
        resumeFilename: form.resumeFilename || null,
      };
      await axios.put(`${API}/api/prospects/user/${userProfile.id}`, payload);
      toast.success('Profile saved');
      refreshProfile?.();
    } catch (e) {
      toast.error('Save failed. Check the fields and try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ResponsiveNavigation />
        <div className="max-w-4xl mx-auto p-6 text-center text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
          Loading your profile…
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="prospect-profile">
      <ResponsiveNavigation />

      <div className="max-w-4xl mx-auto p-4 lg:p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">My Profile</h1>
            <p className="text-sm text-gray-500 mt-1">
              Fill this in once — it prefills every application you send from JobastiIQ.
            </p>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="profile-save-btn"
          >
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : <><Check className="w-4 h-4 mr-2" />Save profile</>}
          </Button>
        </div>

        {/* CV Card */}
        <Card className="mb-6" data-testid="profile-cv-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" /> CV / Resume
            </CardTitle>
            <p className="text-xs text-gray-500 mt-1">
              Uploaded once, reused every time you apply. PDF, DOCX, or TXT — up to 10 MB.
            </p>
          </CardHeader>
          <CardContent>
            {form.resumeUrl ? (
              <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="profile-cv-present">
                <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{form.resumeFilename || 'Your CV'}</p>
                  <p className="text-xs text-gray-500">On file</p>
                </div>
                <button
                  onClick={() => setShowCvViewer(true)}
                  className="p-2 text-blue-600 hover:bg-blue-100 rounded-md"
                  data-testid="profile-cv-view-btn"
                  aria-label="View CV"
                >
                  <Eye className="w-4 h-4" />
                </button>
                <label className="p-2 text-gray-600 hover:bg-gray-100 rounded-md cursor-pointer" title="Replace">
                  <Upload className="w-4 h-4" />
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.txt,.rtf"
                    className="hidden"
                    onChange={(e) => uploadResume(e.target.files?.[0])}
                    data-testid="profile-cv-replace-input"
                  />
                </label>
                <button
                  onClick={removeResume}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-md"
                  data-testid="profile-cv-remove-btn"
                  aria-label="Remove CV"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <label className="border-2 border-dashed border-gray-300 rounded-lg p-6 flex flex-col items-center justify-center text-center hover:border-blue-400 hover:bg-blue-50 transition-colors cursor-pointer" data-testid="profile-cv-empty">
                {uploading ? (
                  <Loader2 className="w-8 h-8 text-blue-500 animate-spin mb-2" />
                ) : (
                  <Upload className="w-8 h-8 text-gray-400 mb-2" />
                )}
                <p className="text-sm text-gray-700 font-medium">{uploading ? 'Uploading…' : 'Upload your CV'}</p>
                <p className="text-xs text-gray-500 mt-1">PDF, DOCX, or TXT · Max 10 MB</p>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.txt,.rtf"
                  className="hidden"
                  disabled={uploading}
                  onChange={(e) => uploadResume(e.target.files?.[0])}
                  data-testid="profile-cv-upload-input"
                />
              </label>
            )}
          </CardContent>
        </Card>

        {/* Personal Info */}
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <User className="w-5 h-5 text-blue-600" /> Personal information
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="firstName">First name</Label>
              <Input id="firstName" value={form.firstName} onChange={onChange('firstName')} data-testid="profile-first-name" />
            </div>
            <div>
              <Label htmlFor="lastName">Last name</Label>
              <Input id="lastName" value={form.lastName} onChange={onChange('lastName')} data-testid="profile-last-name" />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={form.email} readOnly className="bg-gray-50 text-gray-500" />
            </div>
            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input id="phone" value={form.phone} onChange={onChange('phone')} placeholder="+49 …" data-testid="profile-phone" />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="linkedinUrl">LinkedIn profile</Label>
              <Input id="linkedinUrl" value={form.linkedinUrl} onChange={onChange('linkedinUrl')} placeholder="https://linkedin.com/in/…" data-testid="profile-linkedin" />
            </div>
          </CardContent>
        </Card>

        {/* Location & preferences */}
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <MapPin className="w-5 h-5 text-blue-600" /> Location & preferences
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="location">City</Label>
              <Input id="location" value={form.location} onChange={onChange('location')} placeholder="Berlin" data-testid="profile-city" />
            </div>
            <div>
              <Label htmlFor="workArrangement">Work arrangement</Label>
              <select
                id="workArrangement"
                value={form.workArrangement}
                onChange={onChange('workArrangement')}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                data-testid="profile-work-arrangement"
              >
                <option value="">Any</option>
                <option value="On-site">On-site</option>
                <option value="Hybrid">Hybrid</option>
                <option value="Remote">Remote</option>
              </select>
            </div>
            <div>
              <Label htmlFor="availability">Notice period</Label>
              <select
                id="availability"
                value={form.availability}
                onChange={onChange('availability')}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                data-testid="profile-availability"
              >
                <option value="">Select…</option>
                <option value="Immediate">Immediate</option>
                <option value="1 month">1 month</option>
                <option value="2 months">2 months</option>
                <option value="3 months">3 months</option>
              </select>
            </div>
            <div>
              <Label htmlFor="expectedSalaryMin">Expected salary (annual)</Label>
              <div className="flex gap-2">
                <select
                  value={form.salaryCurrency}
                  onChange={onChange('salaryCurrency')}
                  className="h-9 rounded-md border border-input bg-transparent px-2 text-sm shadow-sm"
                  aria-label="Currency"
                >
                  <option value="EUR">€</option>
                  <option value="USD">$</option>
                  <option value="GBP">£</option>
                </select>
                <Input
                  id="expectedSalaryMin"
                  type="number"
                  value={form.expectedSalaryMin}
                  onChange={onChange('expectedSalaryMin')}
                  placeholder="65000"
                  data-testid="profile-salary"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Experience */}
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-blue-600" /> Experience
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="currentJobTitle">Current job title</Label>
              <Input id="currentJobTitle" value={form.currentJobTitle} onChange={onChange('currentJobTitle')} placeholder="Product Manager" data-testid="profile-job-title" />
            </div>
            <div>
              <Label htmlFor="currentCompany">Current company</Label>
              <Input id="currentCompany" value={form.currentCompany} onChange={onChange('currentCompany')} data-testid="profile-company" />
            </div>
            <div>
              <Label htmlFor="experienceLevel">Level</Label>
              <select
                id="experienceLevel"
                value={form.experienceLevel}
                onChange={onChange('experienceLevel')}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                data-testid="profile-experience-level"
              >
                <option value="">Select…</option>
                <option value="junior">Junior</option>
                <option value="mid">Mid</option>
                <option value="senior">Senior</option>
                <option value="lead">Lead / Principal</option>
              </select>
            </div>
            <div>
              <Label htmlFor="experienceYears">Years of experience</Label>
              <Input id="experienceYears" type="number" min="0" value={form.experienceYears} onChange={onChange('experienceYears')} data-testid="profile-experience-years" />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="skills">Skills (comma-separated)</Label>
              <Input id="skills" value={form.skills} onChange={onChange('skills')} placeholder="React, Python, SQL, …" data-testid="profile-skills" />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="languages">Languages spoken (comma-separated)</Label>
              <Input id="languages" value={form.languages} onChange={onChange('languages')} placeholder="English, German, …" data-testid="profile-languages" />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="bio">Short bio</Label>
              <Textarea id="bio" value={form.bio} onChange={onChange('bio')} placeholder="A sentence or two about you — recruiters see this alongside your CV." rows={4} data-testid="profile-bio" />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="profile-save-bottom-btn"
          >
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : <><Check className="w-4 h-4 mr-2" />Save profile</>}
          </Button>
        </div>
      </div>

      {/* CV Viewer Modal */}
      {showCvViewer && form.resumeUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4" onClick={() => setShowCvViewer(false)}>
          <div className="bg-white rounded-lg w-full max-w-4xl h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold text-gray-900">{form.resumeFilename || 'Your CV'}</h3>
              <button onClick={() => setShowCvViewer(false)} className="p-2 text-gray-500 hover:text-gray-800 rounded-md">✕</button>
            </div>
            <iframe src={form.resumeUrl} className="flex-1 w-full" title="CV" />
          </div>
        </div>
      )}
    </div>
  );
};

export default ProspectProfile;
